import { User } from 'lucide-react';
import { useTenantUsers } from '@/hooks/useTasks';
import { TaskPerson } from '../TaskPerson';
import { TaskInlinePicker } from './TaskInlinePicker';
import type { AssignedUser, TenantUser } from '@/types/task';

interface TaskPersonPickerProps {
  value: AssignedUser | null;
  /** A task always has an assignee, so only the reviewer offers a way to empty it. */
  clearable?: boolean;
  disabled?: boolean;
  label: string;
  onChange: (person: TenantUser | null) => void;
}

/**
 * Picks a workspace member.
 *
 * The people come from the member list, which holds back staff and departed
 * members — so someone already on the task who is no longer offered is added
 * back to the list, because the task carries them and the row has to render.
 */
export const TaskPersonPicker = ({
  value, clearable = false, disabled = false, label, onChange,
}: TaskPersonPickerProps) => {
  const { data: users = [] } = useTenantUsers();
  const options = value && !users.some((user) => user.id === value.id)
    ? [ { ...value, email: null } as TenantUser, ...users ]
    : users;

  return (
    <TaskInlinePicker
      label={label}
      selected={value?.id ?? null}
      disabled={disabled}
      clearLabel={clearable ? `Remove ${label.toLowerCase()}` : undefined}
      options={options.map((user) => ({
        value: user.id,
        label: user.display_name,
        node: <TaskPerson person={user} />,
      }))}
      onSelect={(next) => onChange(options.find((user) => user.id === next) ?? null)}
    >
      {value
        ? <TaskPerson person={value} />
        : (
          <>
            <User className="size-4 shrink-0 text-[#9A9A9A]" aria-hidden />
            <span className="text-[13.5px] text-[#9A9A9A]">
              {clearable ? `Add ${label.toLowerCase()}` : 'Unassigned'}
            </span>
          </>
        )}
    </TaskInlinePicker>
  );
};
